import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Mail, User, UserPlus, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerLoginPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please enter email and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/customer/login`, loginData);
      
      if (response.data.success) {
        sessionStorage.setItem('customerToken', response.data.token);
        sessionStorage.setItem('customerEmail', loginData.email);
        sessionStorage.setItem('customerName', response.data.name);
        toast.success('Login successful');
        navigate('/portal/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.name || !registerData.email || !registerData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/customer/register`, {
        name: registerData.name,
        email: registerData.email,
        phone: registerData.phone,
        password: registerData.password
      });
      
      if (response.data.success) {
        toast.success('Account created successfully! Please login.');
        setIsLogin(true);
        setLoginData({ email: registerData.email, password: '' });
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="customer-login-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-block mb-6">
            <div className="flex flex-col items-center">
              <span className="font-serif text-2xl text-white tracking-wide">
                Capital
              </span>
              <span className="text-xs text-[#D4AF37] tracking-[0.2em] uppercase -mt-1">
                Chauffeur Services
              </span>
            </div>
          </Link>
          <h1 className="text-2xl font-serif text-white mb-2">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-[#A3A3A3] text-sm">
            {isLogin 
              ? 'Sign in to view your bookings' 
              : 'Register to manage your reservations'}
          </p>
        </div>

        {/* Toggle Tabs */}
        <div className="flex mb-8 border-b border-white/10">
          <button
            onClick={() => setIsLogin(true)}
            className={`flex-1 pb-4 text-sm font-medium transition-colors ${
              isLogin 
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                : 'text-[#A3A3A3] hover:text-white'
            }`}
          >
            Sign In
          </button>
          <button
            onClick={() => setIsLogin(false)}
            className={`flex-1 pb-4 text-sm font-medium transition-colors ${
              !isLogin 
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                : 'text-[#A3A3A3] hover:text-white'
            }`}
          >
            Register
          </button>
        </div>

        {/* Login Form */}
        {isLogin ? (
          <div className="glass p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                  Email Address
                </Label>
                <Input
                  data-testid="customer-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#D4AF37]" />
                  Password
                </Label>
                <Input
                  data-testid="customer-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter password"
                  className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                />
              </div>

              <Button
                data-testid="customer-login-btn"
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none py-6 text-sm font-medium tracking-wider uppercase disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </div>
        ) : (
          /* Register Form */
          <div className="glass p-8">
            <form onSubmit={handleRegister} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <User className="w-4 h-4 text-[#D4AF37]" />
                  Full Name *
                </Label>
                <Input
                  data-testid="register-name"
                  type="text"
                  value={registerData.name}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                  className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                  Email Address *
                </Label>
                <Input
                  data-testid="register-email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Phone Number</Label>
                <Input
                  data-testid="register-phone"
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 (202) 555-0123"
                  className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#D4AF37]" />
                  Password *
                </Label>
                <Input
                  data-testid="register-password"
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Min 6 characters"
                  className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Confirm Password *</Label>
                <Input
                  data-testid="register-confirm-password"
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm password"
                  className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                />
              </div>

              <Button
                data-testid="customer-register-btn"
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none py-6 text-sm font-medium tracking-wider uppercase disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Account
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Links */}
        <div className="text-center mt-8 space-y-4">
          <Link 
            to="/"
            className="text-[#A3A3A3] hover:text-[#D4AF37] text-sm transition-colors block"
          >
            ← Back to Website
          </Link>
          <Link 
            to="/admin"
            className="text-[#666] hover:text-[#A3A3A3] text-xs transition-colors block"
          >
            Admin Login
          </Link>
        </div>
      </div>
    </div>
  );
}
